import { FuseNavigationItem } from '@fuse/components/navigation/navigation.types';

import { RUTA_CONFIGURACION_REVISION_OPTIMIZACION } from 'app/modules/admin/revision-optimizacion/configuracion-revision-optimizacion.rutas';
import { isAdminRole } from 'app/modules/microservicio/utils/rol-admin';

/** Las entradas del menú lateral. */
export const ENTRADAS_DEL_MENU: FuseNavigationItem[] = [
    {
        id: 'home',
        title: 'Home',
        type: 'basic',
        icon: 'mat_outline:home',
        link: '/home',
    },
    {
        id: 'oven-timeline',
        title: 'Ocupación de horno',
        type: 'basic',
        icon: 'mat_outline:timeline',
        link: '/oven/ocupation',
    },
    {
        id: 'orders',
        title: 'Ordenes',
        type: 'basic',
        icon: 'mat_solid:fastfood',
        link: '/orders',
    },
    {
        id: 'revisiones_y_optimizacion',
        title: 'Revisiones y optimización',
        type: 'collapsable',
        icon: 'mat_solid:view_in_ar',
        children: [
            {
                id: 'revisions',
                title: 'Revisiones',
                type: 'basic',
                icon: 'mat_solid:view_in_ar',
                link: '/revisions',
            },
            {
                id: 'microservicio_agenda',
                title: 'Optimizador MonteCarlo',
                type: 'basic',
                icon: 'heroicons_outline:calendar',
                link: '/microservicio/agenda',
            },
            {
                id: 'microservicio_controlador',
                title: 'Controlador Dinámico',
                type: 'basic',
                icon: 'heroicons_outline:chart-bar',
                link: '/microservicio/controlador',
            },
        ],
    },
    // {
    //     id: 'ovens',
    //     title: 'Hornos',
    //     type: 'basic',
    //     icon: 'mat_outline:local_fire_department',
    //     link: '/oven/ovens',
    // },
    {
        id: 'stock',
        title: 'Stock',
        type: 'collapsable',
        icon: 'feather:package',
        children: [
            {
                id: 'stock_exhibited',
                title: 'Por Producto',
                type: 'basic',
                icon: 'feather:box',
                link: '/stock/agrupated',
            },
            {
                id: 'stock_expired',
                title: 'Por Vencimiento',
                type: 'basic',
                icon: 'feather:box',
                link: '/stock/exhibited',
            },
            {
                id: 'stock_domino',
                title: 'Stock Domino',
                type: 'basic',
                icon: 'feather:box',
                link: '/stock/domino',
            },
            {
                id: 'movements',
                title: 'Movimientos Stock Exhibido',
                type: 'basic',
                icon: 'feather:list',
                link: '/movements/records',
            },
        ],
    },
    {
        id: 'movements',
        title: 'Movimientos Domino',
        type: 'basic',
        icon: 'mat_outline:swap_horiz',
        link: '/movements',
    },
    {
        id: 'gate',
        title: 'Gate de órdenes',
        type: 'collapsable',
        icon: 'heroicons_outline:shield-check',
        children: [
            {
                id: 'gate-decisions',
                title: 'Resultados',
                type: 'basic',
                icon: 'heroicons_outline:chart-bar',
                link: '/gate/decisions',
            },
            {
                id: 'gate-subsidiary-settings',
                title: 'Modo por sucursal',
                type: 'basic',
                icon: 'mat_outline:store',
                link: '/gate-config/subsidiary-settings',
            },
            {
                id: 'gate-rotation-r-values',
                title: 'R por rotación y ciclo',
                type: 'basic',
                icon: 'mat_outline:tune',
                link: '/gate-config/rotation-r-values',
            },
            {
                id: 'gate-rotation-classes',
                title: 'Rotación por producto',
                type: 'basic',
                icon: 'mat_outline:grid_view',
                link: '/gate/rotation-classes',
            },
        ],
    },
    {
        id: 'administration',
        title: 'Administracion',
        type: 'collapsable',
        icon: 'feather:truck',
        children: [
            {
                id: 'products',
                title: 'Productos',
                type: 'basic',
                icon: 'mat_outline:local_drink',
                link: '/products',
            },
            {
                // Solo admin: crear, editar y borrar sucursales es de admin en la API.
                id: 'subsidiaries',
                title: 'Sucursales',
                type: 'basic',
                icon: 'mat_outline:store',
                link: '/subsidiaries',
            },
            {
                id: 'planogram',
                title: 'Planograma',
                type: 'basic',
                icon: 'mat_outline:border_all',
                link: '/planogram',
            },
            {
                // Solo admin: crear, editar y borrar modelos de horno y bandejas es de admin en la
                // API, porque los comparten todas las estaciones que usan el modelo.
                id: 'oven-models',
                title: 'Modelos de horno',
                type: 'basic',
                icon: 'heroicons_outline:fire',
                link: '/oven-models',
            },
            {
                id: 'subsidiary-ovens',
                title: 'Hornos y programas',
                type: 'basic',
                icon: 'mat_outline:local_fire_department',
                link: '/oven/ovens',
            },
            {
                // La ve cualquier rol: la Calibración se lee abierta y la pestaña de
                // Activación avisa por su cuenta a quien no es admin.
                id: 'revision-optimization-config',
                title: 'Configuración de revisión y optimización',
                type: 'basic',
                icon: 'mat_outline:tune',
                link: RUTA_CONFIGURACION_REVISION_OPTIMIZACION,
            },
        ],
    },
];

/** Las entradas que ve solo un admin. */
const SOLO_ADMIN: readonly string[] = ['subsidiaries', 'oven-models'];

/** El menú para un rol: sin las entradas de admin si el rol no lo es, o si todavía no se conoce. */
export function entradasDelMenuPara(role: string | null | undefined): FuseNavigationItem[] {
    if (isAdminRole(role)) {
        return ENTRADAS_DEL_MENU;
    }

    const sinLasDeAdmin = (items: FuseNavigationItem[]): FuseNavigationItem[] =>
        items
            .filter((item) => !SOLO_ADMIN.includes(item.id))
            .map((item) => (item.children ? { ...item, children: sinLasDeAdmin(item.children) } : item));

    return sinLasDeAdmin(ENTRADAS_DEL_MENU);
}
